import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { type Message, formatTimestamp } from "@/lib/askAiUtils";
import { CandidateInfoDisplay } from "./CandidateInfoDisplay";
import { ProcessingInfo } from "./ProcessingInfo";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { useState } from "react";

interface ChatMessageProps {
  message: Message;
  onSendMessage: (message: string) => void;
}

export function ChatMessage({ message, onSendMessage }: ChatMessageProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex gap-2 max-w-[85%] ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarFallback className={`${
            message.type === 'user' 
              ? 'bg-orange-500 text-white' 
              : 'bg-purple-500 text-white'
          }`}>
            {message.type === 'user' ? 'U' : 'AI'}
          </AvatarFallback>
        </Avatar>
        
        <div className={`${message.type === 'user' ? 'text-right' : ''}`}>
          <div className={`rounded-lg p-3 ${
            message.type === 'user' 
              ? 'bg-orange-500 text-white' 
              : 'bg-gray-100 text-gray-900'
          }`}>
            <div className="text-sm leading-relaxed">
              {message.type === 'ai' ? (
                <MarkdownRenderer content={message.content} />
              ) : (
                <div className="whitespace-pre-wrap">{message.content}</div>
              )}
            </div>
            
            {/* Render candidate information if available */}
            {message.candidateInformation && (
              <CandidateInfoDisplay candidateInfo={message.candidateInformation} />
            )}
            
            {/* Render processing info */}
            <ProcessingInfo message={message} />
            
            {message.type === 'ai' && (
              <div className="flex gap-2 mt-3">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs hover:bg-gray-200"
                  onClick={handleCopy}
                  disabled={copied}
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
          
          <div className={`text-xs text-gray-500 mt-1 ${
            message.type === 'user' ? 'text-right' : ''
          }`}>
            {formatTimestamp(message.timestamp)}
          </div>

          {/* Suggestions */}
          {message.suggestions && message.suggestions.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="text-xs text-gray-500">
                Example queries:
              </div>
              {message.suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => onSendMessage(suggestion)}
                  className="block w-full text-left text-xs bg-white border rounded-lg p-2 hover:bg-gray-50 transition-colors"
                  data-testid={`suggestion-${index}`}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
